import { useState, useEffect } from 'react';
import { authFetch, getSession, signOut as apiSignOut, getMyJobs, pollJob, cancelJob } from '../api';

const TOPUP_AMOUNTS = [5, 10, 25, 50, 100];

const s = {
  h1: { fontSize: 26, fontWeight: 600, marginBottom: 4 },
  sub: { color: 'var(--muted)', marginBottom: 32 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16, marginBottom: 32 },
  card: { background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 20 },
  cardLabel: { fontSize: 11, color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 },
  cardVal: { fontSize: 26, fontWeight: 600, color: 'var(--accent2)' },
  cardSub: { fontSize: 12, color: 'var(--muted)', marginTop: 4 },
  section: { marginBottom: 32 },
  sectionTitle: { fontSize: 14, fontWeight: 600, marginBottom: 14 },
  btn: (variant = 'default') => ({
    padding: '8px 18px', borderRadius: 'var(--radius-sm)', fontSize: 13, fontWeight: 500,
    border: variant === 'primary' ? 'none' : `1px solid ${variant === 'danger' ? 'var(--red)' : 'var(--border)'}`,
    background: variant === 'primary' ? 'var(--accent)' : 'transparent',
    color: variant === 'danger' ? 'var(--red)' : variant === 'primary' ? '#fff' : 'var(--text)',
    cursor: 'pointer',
  }),
  keyCard: { background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 18, marginBottom: 12 },
  keyRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 },
  keyPrefix: { fontFamily: 'monospace', fontSize: 14, fontWeight: 500 },
  keyMeta: { fontSize: 12, color: 'var(--muted)', marginTop: 4 },
  pill: (active) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 500, marginLeft: 8,
    background: active ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
    color: active ? 'var(--green)' : 'var(--red)',
  }),
  newKeyBanner: { background: 'rgba(124,111,239,0.08)', border: '1px solid rgba(124,111,239,0.3)', borderRadius: 'var(--radius)', padding: 18, marginBottom: 20 },
  newKeyTitle: { fontWeight: 600, marginBottom: 8, color: 'var(--accent2)', fontSize: 14 },
  newKeyVal: { fontFamily: 'monospace', fontSize: 12, background: 'var(--bg)', padding: '8px 12px', borderRadius: 'var(--radius-sm)', wordBreak: 'break-all', marginBottom: 10, lineHeight: 1.6 },
  topupGrid: { display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 },
  topupBtn: (selected) => ({
    padding: '10px 20px', borderRadius: 'var(--radius-sm)', fontWeight: 600, fontSize: 14,
    border: `1px solid ${selected ? 'var(--accent)' : 'var(--border)'}`,
    background: selected ? 'rgba(124,111,239,0.12)' : 'transparent',
    color: selected ? 'var(--accent2)' : 'var(--text)',
    cursor: 'pointer',
  }),
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid var(--border)', color: 'var(--muted)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase' },
  td: { padding: '8px 12px', borderBottom: '1px solid var(--border)' },
  loginBtn: { padding: '10px 24px', borderRadius: 'var(--radius-sm)', border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer' },
  loginInput: { width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--text)', fontSize: 14, outline: 'none', marginBottom: 12 },
};

function handleSignOut(setConsumerId, setData, setError) {
  apiSignOut();
  setConsumerId('');
  setData(null);
  setError('');
}

export default function ConsumerDashboard({ navigate }) {
  const session = getSession();
  const [consumerId, setConsumerId] = useState(() => session?.consumerId || localStorage.getItem('am_consumer_id') || '');
  const [inputId, setInputId]       = useState('');
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [newKey, setNewKey]         = useState(null);
  const [copied, setCopied]         = useState(false);
  const [topupAmount, setTopupAmount] = useState(10);
  const [toppingUp, setToppingUp]   = useState(false);
  const [disputeCall, setDisputeCall] = useState(null);
  const [disputeReason, setDisputeReason] = useState('');
  const [disputeSubmitting, setDisputeSubmitting] = useState(false);
  const [disputeMsg, setDisputeMsg] = useState('');
  const [myJobs, setMyJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [expandedJob, setExpandedJob] = useState(null);

  useEffect(() => { if (consumerId) load(consumerId); }, [consumerId]);

  // Load jobs when consumer is set
  useEffect(() => {
    if (!consumerId) return;
    setJobsLoading(true);
    getMyJobs().then(data => { if (Array.isArray(data)) setMyJobs(data); setJobsLoading(false); })
      .catch(() => setJobsLoading(false));
  }, [consumerId]);

  // Poll active jobs
  useEffect(() => {
    const active = myJobs.filter(j => j.status === 'pending' || j.status === 'running');
    if (active.length === 0) return;
    const interval = setInterval(async () => {
      const updated = await Promise.all(myJobs.map(async j => {
        if (j.status === 'pending' || j.status === 'running') {
          try { return await pollJob(j.id); } catch { return j; }
        }
        return j;
      }));
      setMyJobs(updated);
    }, 3000);
    return () => clearInterval(interval);
  }, [myJobs.filter(j => j.status === 'pending' || j.status === 'running').length]); // eslint-disable-line

  async function load(id) {
    setLoading(true); setError('');
    try {
      const r = await authFetch(`/v1/consumer/${id}`);
      const d = await r.json();
      if (!r.ok) {
        setError(d.error || `Error ${r.status}`);
        // If 404, clear stored ID so they can try again
        if (r.status === 404 || r.status === 401) {
          apiSignOut();
          setConsumerId('');
        }
        setLoading(false);
        return;
      }
      setData(d);
    } catch { setError('Could not reach server — is the backend running?'); }
    setLoading(false);
  }

  async function submitDispute() {
    if (!disputeCall || !disputeReason.trim()) return;
    setDisputeSubmitting(true); setDisputeMsg('');
    try {
      const r = await authFetch('/v1/disputes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ callId: disputeCall.id, reason: disputeReason.trim() }),
      });
      const d = await r.json();
      if (d.error) { setDisputeMsg(d.error); }
      else {
        setDisputeMsg('Dispute filed successfully. We will review within 48 hours.');
        setDisputeCall(null); setDisputeReason('');
        load(consumerId); // reload to update UI
      }
    } catch { setDisputeMsg('Failed to submit dispute'); }
    setDisputeSubmitting(false);
  }

  function login() {
    const id = inputId.trim();
    if (!id) return;
    localStorage.setItem('am_consumer_id', id);
    setConsumerId(id);
    setInputId('');
  }

  function copyKey(key) {
    navigator.clipboard.writeText(key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function rotateKey(keyId) {
    if (!window.confirm('Rotate this key? The old key stops working immediately.')) return;
    const r = await authFetch(`/v1/consumer/${consumerId}/keys/${keyId}/rotate`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ label: 'Rotated key' })
    });
    const d = await r.json();
    if (d.apiKey) { setNewKey(d); load(consumerId); }
    else setError(d.error || 'Rotate failed');
  }

  async function revokeKey(keyId) {
    if (!window.confirm('Revoke this key permanently? Cannot be undone.')) return;
    await authFetch(`/v1/consumer/${consumerId}/keys/${keyId}/revoke`, { method: 'POST' });
    load(consumerId);
  }

  async function addKey() {
    const label = window.prompt('Label for new key:', 'New key');
    if (!label) return;
    const r = await authFetch(`/v1/consumer/${consumerId}/keys`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ label })
    });
    const d = await r.json();
    if (d.apiKey) { setNewKey(d); load(consumerId); }
    else setError(d.error || 'Failed to create key');
  }

  async function topup() {
    setToppingUp(true); setError('');
    try {
      const r = await authFetch('/v1/billing/topup', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amountUsd: topupAmount }),
      });
      const d = await r.json();
      if (d.url && d.url.startsWith('https://checkout.stripe.com')) window.location.href = d.url;
      else if (d.dev_mode) { alert(`Dev mode: $${topupAmount} credits added!`); load(consumerId); }
      else setError(d.error || 'Top-up failed');
    } catch { setError('Top-up request failed'); }
    setToppingUp(false);
  }

  // ── Login screen ────────────────────────────────────────────────────────
  if (!consumerId) return (
    <div style={{ maxWidth: 440 }}>
      <h1 style={s.h1}>My Account</h1>
      <p style={{ ...s.sub, marginBottom: 24 }}>
        Enter your Consumer ID to manage credits and API keys.
      </p>
      <input
        style={s.loginInput}
        value={inputId}
        onChange={e => setInputId(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && login()}
        placeholder="Consumer ID (e.g. e7d6abe8-...)"
        autoFocus
      />
      {error && (
        <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 14, padding: '10px 14px', background: 'rgba(239,68,68,0.08)', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(239,68,68,0.2)' }}>
          {error}
        </div>
      )}
      <button style={s.loginBtn} onClick={login} disabled={!inputId.trim()}>
        View Account →
      </button>
      <div style={{ marginTop: 20, fontSize: 13, color: 'var(--muted)' }}>
        Don't have an ID? <span style={{ color: 'var(--accent2)', cursor: 'pointer' }} onClick={() => navigate('getkey')}>Get an API key</span> to create one.
      </div>
    </div>
  );

  if (loading && !data) return <div style={{ color: 'var(--muted)', padding: 40 }}>Loading...</div>;

  if (!data) return (
    <div style={{ maxWidth: 440 }}>
      <div style={{ color: 'var(--red)', marginBottom: 16, fontSize: 14 }}>{error || 'Could not load account'}</div>
      <button style={s.loginBtn} onClick={() => handleSignOut(setConsumerId, setData, setError)}>
        Try a different ID
      </button>
    </div>
  );

  const activeKeys  = data.keys?.filter(k => k.is_active)  || [];
  const revokedKeys = data.keys?.filter(k => !k.is_active) || [];

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={s.h1}>My Account</h1>
          <div style={{ fontSize: 13, color: 'var(--muted)' }}>{data.email}</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button style={s.btn()} onClick={() => load(consumerId)}>↻ Refresh</button>
          <button style={s.btn()} onClick={() => handleSignOut(setConsumerId, setData, setError)}>Sign out</button>
        </div>
      </div>

      {/* New key reveal banner */}
      {newKey && (
        <div style={s.newKeyBanner}>
          <div style={s.newKeyTitle}>⚠ Save your new API key — shown only once</div>
          <div style={s.newKeyVal}>{newKey.apiKey}</div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button style={s.btn('primary')} onClick={() => copyKey(newKey.apiKey)}>
              {copied ? '✓ Copied' : 'Copy to clipboard'}
            </button>
            <button style={s.btn()} onClick={() => setNewKey(null)}>Dismiss</button>
          </div>
        </div>
      )}

      {/* Stats */}
      <div style={s.grid}>
        {[
          { label: 'Credit balance', val: `$${(data.credit_balance ?? 0).toFixed(4)}`, sub: 'available to spend' },
          { label: 'Total spent',    val: `$${(data.total_spent ?? 0).toFixed(2)}`,     sub: 'all-time' },
          { label: 'Active keys',    val: activeKeys.length,                             sub: `of 5 max` },
          { label: 'Recent calls',   val: data.recent_calls?.length ?? 0,               sub: 'last 20 shown' },
        ].map(item => (
          <div key={item.label} style={s.card}>
            <div style={s.cardLabel}>{item.label}</div>
            <div style={s.cardVal}>{item.val}</div>
            <div style={s.cardSub}>{item.sub}</div>
          </div>
        ))}
      </div>

      {/* Top up */}
      <div style={s.section}>
        <div style={s.sectionTitle}>Add credits</div>
        <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 20 }}>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 14 }}>
            Credits never expire. $1 = $1 of API calls. Works across all builds.
          </div>
          <div style={s.topupGrid}>
            {TOPUP_AMOUNTS.map(amt => (
              <button key={amt} style={s.topupBtn(topupAmount === amt)} onClick={() => setTopupAmount(amt)}>
                ${amt}
              </button>
            ))}
          </div>
          {error && <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 12 }}>{error}</div>}
          <button style={s.btn('primary')} onClick={topup} disabled={toppingUp}>
            {toppingUp ? 'Redirecting...' : `Add $${topupAmount} credits →`}
          </button>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 10 }}>
            Secure checkout via Stripe. No subscription.
          </div>
        </div>
      </div>

      {/* API Keys */}
      <div style={s.section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={s.sectionTitle}>API keys</div>
          {activeKeys.length < 5 && (
            <button style={s.btn()} onClick={addKey}>+ New key</button>
          )}
        </div>
        <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 14, lineHeight: 1.6 }}>
          Keys are stored as hashes — we can never see or recover them. If leaked, rotate immediately.
          The old key stops working the instant you rotate.
        </div>

        {activeKeys.map(k => (
          <div key={k.id} style={s.keyCard}>
            <div style={s.keyRow}>
              <div>
                <span style={s.keyPrefix}>{k.key_prefix}...</span>
                <span style={s.pill(true)}>active</span>
                <div style={s.keyMeta}>
                  {k.label} · {k.call_count ?? 0} calls
                  {k.last_used_at && ` · last used ${new Date(k.last_used_at).toLocaleDateString()}`}
                  {k.last_used_ip && ` · from ${k.last_used_ip}`}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={s.btn()} onClick={() => rotateKey(k.id)}>Rotate</button>
                <button style={s.btn('danger')} onClick={() => revokeKey(k.id)}>Revoke</button>
              </div>
            </div>
          </div>
        ))}

        {revokedKeys.length > 0 && (
          <details style={{ marginTop: 8 }}>
            <summary style={{ color: 'var(--muted)', fontSize: 13, cursor: 'pointer', padding: '8px 0' }}>
              {revokedKeys.length} revoked key{revokedKeys.length > 1 ? 's' : ''}
            </summary>
            {revokedKeys.map(k => (
              <div key={k.id} style={{ ...s.keyCard, opacity: 0.45, marginTop: 8 }}>
                <span style={s.keyPrefix}>{k.key_prefix}...</span>
                <span style={s.pill(false)}>revoked</span>
                <div style={s.keyMeta}>
                  {k.label} · revoked {k.revoked_at ? new Date(k.revoked_at).toLocaleDateString() : '—'}
                </div>
              </div>
            ))}
          </details>
        )}
      </div>

      {/* Dispute modal */}
      {disputeCall && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
             onClick={() => setDisputeCall(null)}>
          <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 28, maxWidth: 460, width: '90%' }}
               onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 6 }}>Dispute a charge</div>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>
              Call to <strong>{disputeCall.slug}</strong> on {new Date(disputeCall.called_at).toLocaleString()} —
              charged <strong style={{ color: 'var(--accent2)' }}>${(disputeCall.credits_charged || 0).toFixed(4)}</strong>
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}>Request ID: <code style={{ fontSize: 11 }}>{disputeCall.request_id || 'N/A'}</code></div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 16 }}>
              Response: {disputeCall.status} · {disputeCall.duration_ms}ms · {((disputeCall.response_bytes || 0) / 1024).toFixed(1)}KB
            </div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6 }}>What went wrong?</label>
            <textarea style={{ width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, outline: 'none', resize: 'vertical', minHeight: 80, boxSizing: 'border-box', marginBottom: 10 }}
                      value={disputeReason} onChange={e => setDisputeReason(e.target.value)}
                      placeholder="e.g., Response was empty, returned wrong data, build returned an error but I was still charged..."
                      maxLength={500} />
            {disputeMsg && <div style={{ fontSize: 12, color: disputeMsg.includes('success') ? 'var(--green)' : 'var(--red)', marginBottom: 8 }}>{disputeMsg}</div>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={submitDispute} disabled={!disputeReason.trim() || disputeSubmitting}
                      style={{ padding: '9px 20px', borderRadius: 'var(--radius-sm)', border: 'none', background: 'var(--red)', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer', opacity: !disputeReason.trim() ? 0.5 : 1 }}>
                {disputeSubmitting ? 'Filing...' : 'File Dispute'}
              </button>
              <button onClick={() => setDisputeCall(null)}
                      style={{ padding: '9px 20px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'transparent', color: 'var(--muted)', fontSize: 13, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recent calls */}
      <div style={s.section}>
        <div style={s.sectionTitle}>Recent calls</div>
        <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
          <table style={s.table}>
            <thead>
              <tr>{['Time', 'Build', 'Status', 'Duration', 'Size', 'Charged', ''].map(h =>
                <th key={h} style={s.th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {!data.recent_calls?.length ? (
                <tr><td colSpan={7} style={{ ...s.td, color: 'var(--muted)', textAlign: 'center', padding: 32 }}>
                  No calls yet — make your first API call to see activity here.
                </td></tr>
              ) : data.recent_calls.map((c, i) => {
                const ok = c.status >= 200 && c.status < 300;
                const charged = c.credits_charged > 0;
                const canDispute = charged && !c.has_dispute;
                // Check if within 72h
                const callAge = Date.now() - new Date(c.called_at + 'Z').getTime();
                const withinWindow = callAge < 72 * 60 * 60 * 1000;
                return (
                  <tr key={i}>
                    <td style={{ ...s.td, fontSize: 12, color: 'var(--muted)' }}>
                      <div>{new Date(c.called_at).toLocaleTimeString()}</div>
                      <div style={{ fontSize: 10, color: 'var(--muted)' }}>{new Date(c.called_at).toLocaleDateString()}</div>
                    </td>
                    <td style={{ ...s.td, fontFamily: 'monospace', fontSize: 12 }}>{c.slug}</td>
                    <td style={s.td}>
                      <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 500, background: ok ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)', color: ok ? 'var(--green)' : 'var(--red)' }}>
                        {c.status || 'ERR'}
                      </span>
                      {!ok && !charged && <span style={{ fontSize: 10, color: 'var(--green)', marginLeft: 4 }}>refunded</span>}
                    </td>
                    <td style={{ ...s.td, color: 'var(--muted)', fontSize: 12 }}>{c.duration_ms ? `${c.duration_ms}ms` : '\u2014'}</td>
                    <td style={{ ...s.td, color: 'var(--muted)', fontSize: 11 }}>
                      {c.response_bytes ? `${(c.response_bytes / 1024).toFixed(1)}KB` : '\u2014'}
                    </td>
                    <td style={{ ...s.td, color: charged ? 'var(--accent2)' : 'var(--muted)' }}>
                      {charged ? `-$${c.credits_charged.toFixed(4)}` : '$0.00'}
                    </td>
                    <td style={{ ...s.td, textAlign: 'right' }}>
                      {c.has_dispute ? (
                        <span style={{ fontSize: 11, color: 'var(--yellow, #facc15)', padding: '2px 8px', borderRadius: 99, background: 'rgba(234,179,8,0.1)' }}>disputed</span>
                      ) : canDispute && withinWindow ? (
                        <button onClick={() => { setDisputeCall(c); setDisputeReason(''); setDisputeMsg(''); }}
                                style={{ padding: '3px 10px', fontSize: 11, border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', background: 'transparent', color: 'var(--muted)', cursor: 'pointer' }}>
                          Dispute
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 8 }}>
          Failed calls (4xx/5xx) are automatically refunded. You can dispute successful calls within 72 hours.
        </div>
      </div>

      {/* Jobs */}
      <div style={s.section}>
        <div style={s.sectionTitle}>Jobs</div>
        <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
          <table style={s.table}>
            <thead>
              <tr>{['Time', 'Build', 'Job Type', 'Status', 'Progress', 'Cost', ''].map(h =>
                <th key={h} style={s.th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {jobsLoading ? (
                <tr><td colSpan={7} style={{ ...s.td, color: 'var(--muted)', textAlign: 'center', padding: 32 }}>Loading jobs...</td></tr>
              ) : !myJobs.length ? (
                <tr><td colSpan={7} style={{ ...s.td, color: 'var(--muted)', textAlign: 'center', padding: 32 }}>
                  No jobs yet. Submit a job from a build's detail page.
                </td></tr>
              ) : myJobs.map((j, i) => {
                const isActive = j.status === 'pending' || j.status === 'running';
                const isDone = j.status === 'completed';
                const isFail = j.status === 'failed' || j.status === 'timeout' || j.status === 'cancelled';
                return (
                  <tr key={j.id || i}>
                    <td style={{ ...s.td, fontSize: 12, color: 'var(--muted)' }}>
                      <div>{j.submitted_at ? new Date(j.submitted_at).toLocaleTimeString() : ''}</div>
                      <div style={{ fontSize: 10 }}>{j.submitted_at ? new Date(j.submitted_at).toLocaleDateString() : ''}</div>
                    </td>
                    <td style={{ ...s.td, fontFamily: 'monospace', fontSize: 12 }}>{j.build_slug}</td>
                    <td style={{ ...s.td, fontSize: 12 }}>{j.job_type_name || j.job_type}</td>
                    <td style={s.td}>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 500,
                        background: isDone ? 'rgba(34,197,94,0.12)' : isActive ? 'rgba(59,130,246,0.12)' : 'rgba(239,68,68,0.12)',
                        color: isDone ? 'var(--green)' : isActive ? '#60a5fa' : 'var(--red)',
                      }}>{j.status}</span>
                    </td>
                    <td style={{ ...s.td, minWidth: 100 }}>
                      {isActive ? (
                        <div>
                          <div style={{ height: 4, background: 'var(--bg3)', borderRadius: 2, overflow: 'hidden' }}>
                            <div style={{ width: `${j.progress || 0}%`, height: '100%', background: 'var(--accent)', borderRadius: 2, transition: 'width 0.5s' }} />
                          </div>
                          <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>{j.progress || 0}%</div>
                        </div>
                      ) : isDone ? '100%' : <span style={{ color: 'var(--muted)' }}>{'\u2014'}</span>}
                    </td>
                    <td style={{ ...s.td, color: j.credits_charged > 0 ? 'var(--accent2)' : j.credits_reserved > 0 && isFail ? 'var(--green)' : 'var(--muted)' }}>
                      {j.credits_charged > 0 ? `-$${j.credits_charged.toFixed(2)}` :
                       isFail && j.credits_reserved > 0 ? 'refunded' :
                       isActive ? `$${j.credits_reserved.toFixed(2)} reserved` : '$0.00'}
                    </td>
                    <td style={{ ...s.td, textAlign: 'right' }}>
                      {isDone && (
                        <button onClick={() => setExpandedJob(expandedJob === j.id ? null : j.id)}
                          style={{ padding: '3px 10px', fontSize: 11, border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', background: 'transparent', color: 'var(--accent2)', cursor: 'pointer' }}>
                          {expandedJob === j.id ? 'Hide' : 'Result'}
                        </button>
                      )}
                      {isActive && (
                        <button onClick={async () => { await cancelJob(j.id); const updated = await getMyJobs(); if (Array.isArray(updated)) setMyJobs(updated); }}
                          style={{ padding: '3px 10px', fontSize: 11, border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', background: 'transparent', color: 'var(--red)', cursor: 'pointer' }}>
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          {/* Expanded job result */}
          {expandedJob && (() => {
            const j = myJobs.find(j => j.id === expandedJob);
            if (!j) return null;
            return (
              <div style={{ padding: '16px 20px', borderTop: '1px solid var(--border)', background: 'var(--bg)' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', marginBottom: 8, textTransform: 'uppercase' }}>Job Result</div>
                <pre style={{
                  fontFamily: 'monospace', fontSize: 12, color: 'var(--accent2)',
                  whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: 300, overflowY: 'auto',
                  margin: 0,
                }}>{j.result ? (typeof j.result === 'string' ? j.result : JSON.stringify(j.result, null, 2)) : 'No result data'}</pre>
              </div>
            );
          })()}
        </div>
        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 8 }}>
          Failed and timed out jobs are automatically refunded. Active jobs update every 3 seconds.
        </div>
      </div>
    </div>
  );
}
